/**
 * StarButton — THE personal star (decision 38, Library LB6-T5, L6-09).
 *
 * One component for every star in the Library: document rows (files, synced
 * documents, articles — kind 'document'), the document page and the article
 * reader headers (kind 'document'), folder rows in the sidebar (kind
 * 'folder'), and the Favorites page's rows (the kind the API listed them
 * under). It is handed its item — `{ id, kind }`, both from the row it sits
 * on — and never guesses the kind.
 *
 * It shows what the server holds (useStarState reads the favourites query) and
 * changes it only through the store's `star` / `unstar` pair, which await
 * the request: success is announced only after the server answers (the toast
 * lives in the pair, never here), a failure is an error toast and the star
 * stays as the server has it. While a write for this item is in flight — asked
 * by this star or by any other control (the store holds the one guard) — the
 * button is busy and a click sends nothing.
 *
 * Until the favourites have been read, or when the read failed, the star does
 * not know, and says so: disabled, neither lit nor unlit, labelled as loading
 * or as failed (LB6-T5-R-01 / BF-01). It never shows a failed read as
 * 'not starred', and never acts on one.
 */

import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  star, unstar, useStarState, useStarWriteInFlight, type StarTarget,
} from '@/lib/favorites-store';

interface StarButtonProps {
  /** The item this star names: its id and its kind, from its row. */
  item: StarTarget;
  size?: 'sm' | 'md';
  className?: string;
  showOnHoverOnly?: boolean;
  /** The star sits on a primary-filled (selected) row: its unlit outline takes the row's foreground. */
  onPrimary?: boolean;
}

const LABELS = {
  starred: 'Remove from favorites',
  unstarred: 'Add to favorites',
  pending: 'Loading favorites',
  failed: "Couldn't load favorites",
} as const;

export function StarButton({ item, size = 'sm', className, showOnHoverOnly, onPrimary }: StarButtonProps) {
  const state = useStarState(item);
  const busy = useStarWriteInFlight(item);
  const known = state === 'starred' || state === 'unstarred';
  const isStarred = state === 'starred';

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Nothing is sent on a read that has not answered (the button is disabled
    // too); the store ignores a second write while one is in flight.
    if (!known || busy) return;
    await (isStarred ? unstar(item) : star(item));
  };

  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const btnSize = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';
  const label = LABELS[state];

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        btnSize,
        'shrink-0 transition-all',
        isStarred
          ? 'text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300'
          : onPrimary
            ? 'text-primary-foreground/70 hover:text-primary-foreground'
            : 'text-muted-foreground/40 hover:text-amber-500 dark:hover:text-amber-400',
        showOnHoverOnly && !isStarred && 'opacity-0 group-hover:opacity-100 focus:opacity-100',
        busy && 'animate-pulse',
        className,
      )}
      onClick={handleClick}
      disabled={!known}
      aria-label={label}
      aria-pressed={known ? isStarred : undefined}
      aria-busy={busy || state === 'pending' || undefined}
      data-star-kind={item.kind}
      data-star-state={state}
      title={label}
    >
      <Star
        className={cn(iconSize, isStarred && 'fill-current')}
      />
    </Button>
  );
}
